import { z } from 'zod';

/** Акт на дополнительные работы с площадки (ПР-20) */
export const EXTRA_WORK_ACT_STATUSES = {
  draft: 'Ждёт подписи',
  returned: 'Возвращён с замечанием',
  signed: 'Подписан',
} as const;
export type ExtraWorkActStatus = keyof typeof EXTRA_WORK_ACT_STATUSES;

export const EXTRA_WORK_SIGNER_KINDS = {
  customer: 'Заказчик',
  tech_supervision: 'Технадзор',
} as const;
export type ExtraWorkSignerKind = keyof typeof EXTRA_WORK_SIGNER_KINDS;

const DateStr = z.iso.date();

export const ExtraWorkActInput = z.strictObject({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  actDay: DateStr,
  title: z.string().trim().min(3).max(300),
  description: z.string().trim().min(3).max(5000),
  activityId: z.uuid(),
  contractId: z.uuid(),
  laborNote: z.string().trim().max(2000).nullable().optional(),
  machinesNote: z.string().trim().max(2000).nullable().optional(),
  materialsNote: z.string().trim().max(2000).nullable().optional(),
});
export type ExtraWorkActInput = z.infer<typeof ExtraWorkActInput>;

export const ExtraWorkActSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  actDay: DateStr,
  title: z.string(),
  status: z.enum(Object.keys(EXTRA_WORK_ACT_STATUSES) as [ExtraWorkActStatus, ...ExtraWorkActStatus[]]),
  activityId: z.uuid(),
  activityCode: z.string(),
  activityName: z.string(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  changeId: z.uuid().nullable(),
  changeNumber: z.string().nullable(),
  signedAt: z.string().nullable(),
  signerKind: z
    .enum(Object.keys(EXTRA_WORK_SIGNER_KINDS) as [ExtraWorkSignerKind, ...ExtraWorkSignerKind[]])
    .nullable(),
  canSign: z.boolean(),
  /** Возврат с замечанием: что не так (видно, пока акт не подписан) */
  returnComment: z.string().nullable(),
  returnedAt: z.string().nullable(),
});
export type ExtraWorkActSummary = z.infer<typeof ExtraWorkActSummary>;

export const ExtraWorkActDetail = ExtraWorkActSummary.extend({
  objectId: z.uuid().nullable(),
  objectName: z.string().nullable(),
  description: z.string(),
  laborNote: z.string().nullable(),
  machinesNote: z.string().nullable(),
  materialsNote: z.string().nullable(),
  signedByName: z.string().nullable(),
  /** Контрольная сумма содержания (sha256): у подписанного — на момент подписи (у старых её нет), у остальных — текущая */
  contentHash: z.string().nullable(),
  returnedByName: z.string().nullable(),
  /** Может ли человек вернуть акт с замечанием (заказчик или технадзор, пока не подписан) */
  canReturn: z.boolean(),
  /** Прораб может править черновик и возвращённый акт */
  canEdit: z.boolean(),
  createdAt: z.string(),
});
export type ExtraWorkActDetail = z.infer<typeof ExtraWorkActDetail>;

export const ExtraWorkActListQuery = z.strictObject({
  day: DateStr.optional(),
  status: z
    .enum(Object.keys(EXTRA_WORK_ACT_STATUSES) as [ExtraWorkActStatus, ...ExtraWorkActStatus[]])
    .optional(),
});
export type ExtraWorkActListQuery = z.infer<typeof ExtraWorkActListQuery>;

export const ExtraWorkActReturn = z.strictObject({
  comment: z.string().trim().min(3).max(2000),
});
export type ExtraWorkActReturn = z.infer<typeof ExtraWorkActReturn>;

/** Правка акта прорабом: тот же состав полей, что при создании, без проекта */
export const ExtraWorkActPatch = ExtraWorkActInput.omit({ projectId: true });
export type ExtraWorkActPatch = z.infer<typeof ExtraWorkActPatch>;

/** Отметка «заказчик подключён к порталу» на проекте (ВК-12) */
export const CustomerConnection = z.object({
  connected: z.boolean(),
  connectedAt: z.string().nullable(),
  connectedByName: z.string().nullable(),
});
export type CustomerConnection = z.infer<typeof CustomerConnection>;
export const CustomerConnectionInput = z.strictObject({ connected: z.boolean() });
export type CustomerConnectionInput = z.infer<typeof CustomerConnectionInput>;

/** Подпись: контрольная сумма того содержания, которое человек видел на экране; при расхождении подпись отклоняется */
export const ExtraWorkActSign = z.strictObject({ expectedHash: z.string().length(64).optional() });
export type ExtraWorkActSign = z.infer<typeof ExtraWorkActSign>;

/** Номер акта: АДР-001 */
export function extraWorkActNumber(seq: number): string {
  return `АДР-${String(seq).padStart(3, '0')}`;
}
